import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication, EventType, AccountInfo } from "@azure/msal-browser";

import { loadConfig, getConfig } from "./config";
import { buildMsalConfig } from "./auth/authConfig";
import App from "./App";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function shouldBypassMsal(): boolean {
  const isDev = process.env.NODE_ENV === "development";
  const cfg = getConfig();
  const hasCredentials = !!(cfg.clientId && cfg.tenantId);
  return isDev && !hasCredentials;
}

function renderDevApp() {
  console.warn("DEV MODE: MSAL bypassed.");
  const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <FluentProvider theme={webLightTheme}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </FluentProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
}

async function renderMsalApp() {
  const msalInstance = new PublicClientApplication(buildMsalConfig());
  await msalInstance.initialize();
  const response = await msalInstance.handleRedirectPromise();

  if (response?.account) {
    msalInstance.setActiveAccount(response.account);
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as { account: AccountInfo };
      if (payload.account) {
        msalInstance.setActiveAccount(payload.account);
      }
    }
  });

  const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <FluentProvider theme={webLightTheme}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </FluentProvider>
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>
  );
}

// Load runtime config first, then boot the app
loadConfig().then(() => {
  if (shouldBypassMsal()) {
    renderDevApp();
  } else {
    renderMsalApp().catch((error) => {
      console.error("Failed to initialize MSAL:", error);
      document.getElementById("root")!.innerHTML = `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif;">
          <h1>Failed to Initialize</h1>
          <p>Please refresh the page. If the problem persists, contact support.</p>
          <button onclick="location.reload()">Refresh</button>
        </div>
      `;
    });
  }
});
